/**
 * Position the popover on the correct edge within the reference rectangle.
 */
import { stringLiteralArray } from "@oui/system/utils"
import { toClientRect } from "./rect-utils"
import { clamp, curry } from "ramda"

export type Edge = typeof edges[number]

// Edges are in a clockwise order to mirror CSS shorthand syntax for margin and
// padding.
const edges = stringLiteralArray(["top", "right", "bottom", "left"])

const constrain = curry((a: number, b: number, n: number) => {
  const min = Math.min(a, b)
  const max = Math.max(a, b)
  return clamp(min, max, n)
})

const isHorizontal = (edge: Edge) => ["left", "right"].indexOf(edge) !== -1

const getPosition = (
  [primaryEdge, secondaryEdge]: [Edge, Edge],
  anchorRect: ClientRect,
  elementRect: ClientRect,
  offset: ClientRect
) => {
  const center = {
    top: anchorRect.top + anchorRect.height / 2 - elementRect.height / 2,
    left: anchorRect.left + anchorRect.width / 2 - elementRect.width / 2
  }
  const primaryPosition = {
    top: {
      top: offset.bottom - elementRect.height,
      left: center.left
    },
    right: {
      top: center.top,
      left: offset.left
    },
    bottom: {
      top: offset.top,
      left: center.left
    },
    left: {
      top: center.top,
      left: offset.right - elementRect.width
    }
  }
  const secondaryPosition = {
    top: {
      top: anchorRect.top
    },
    right: {
      left: anchorRect.right - elementRect.width
    },
    bottom: {
      top: anchorRect.bottom - elementRect.height
    },
    left: {
      left: anchorRect.left
    }
  }

  const isPrimaryHorizontal = isHorizontal(primaryEdge)
  const isSecondaryHorizontal = isHorizontal(secondaryEdge)
  const axisToConstrain = isPrimaryHorizontal ? "top" : "left"
  const isCentered =
    primaryEdge === secondaryEdge ||
    isPrimaryHorizontal === isSecondaryHorizontal
  const preferredPosition = {
    ...offset,
    ...primaryPosition[primaryEdge],
    ...(isCentered ? {} : secondaryPosition[secondaryEdge])
  }

  const constrainSecondaryAxis = isPrimaryHorizontal
    ? constrain(
        Math.min(offset.top, offset.bottom - elementRect.height),
        Math.max(offset.top, offset.bottom - elementRect.height)
      )
    : constrain(
        Math.min(offset.left, offset.right - elementRect.width),
        Math.max(offset.left, offset.right - elementRect.width)
      )

  return toClientRect({
    ...preferredPosition,
    [axisToConstrain]: constrainSecondaryAxis(
      preferredPosition[axisToConstrain]
    )
  })
}

export { edges }
export default getPosition
